import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import PasswordRequirementsHint from '@/components/PasswordRequirementsHint'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

export default function Register() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  /** When true, email verification was bypassed (feature flag on); show no email-related message. */
  const [emailVerificationBypassed, setEmailVerificationBypassed] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    })

    if (signUpError) {
      setError(signUpError.message)
      setLoading(false)
      return
    }

    const url = `${SUPABASE_URL}/functions/v1/confirm-email-bypass`
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (data.session?.access_token) {
      headers.Authorization = `Bearer ${data.session.access_token}`
    }
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
    })
    const result = (await res.json().catch(() => ({}))) as { confirmed?: boolean; error?: string }
    if (result.confirmed) {
      if (data.session?.access_token) await supabase.auth.refreshSession()
      setEmailVerificationBypassed(true)
    }

    setSuccess(true)
    setLoading(false)
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">
          {emailVerificationBypassed ? 'Account created' : 'Check your email'}
        </h1>
        <p className="mb-4">
          {emailVerificationBypassed
            ? 'You can sign in now. Click below to go to the login page.'
            : "We've sent you a verification link. Please check your email to verify your account."}
        </p>
        <button
          onClick={() => navigate('/login')}
          className="btn-primary"
        >
          Go to Login
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6">Register</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block mb-2 font-semibold">
            Email
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-required="true"
            className="w-full"
            autoComplete="email"
          />
        </div>

        <div>
          <label htmlFor="password" className="block mb-2 font-semibold">
            Password
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <div className="flex gap-2">
            <input
              type={passwordVisible ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-required="true"
              minLength={8}
              className="w-full flex-1"
              autoComplete="new-password"
              aria-describedby="password-hint"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              className="px-3 py-2 rounded border shrink-0"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            >
              {passwordVisible ? 'Hide' : 'Show'}
            </button>
          </div>
          <PasswordRequirementsHint id="password-hint" />
        </div>

        {error && (
          <div role="alert" className="text-color-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-bg-interactive text-text-inverse hover:bg-bg-interactive-hover disabled:opacity-50"
        >
          {loading ? 'Creating account...' : 'Register'}
        </button>
      </form>
    </div>
  )
}

